import { formatDuration } from '../../../lib/dateUtils';
import type { CreativesListData } from './api';
import { CREATIVE_SCORE_COMPONENT_ORDER, creativeScoreComponentLabel } from './creativeScore';
import { formatDelta, formatMetricValue } from './format';

const CSV_MIME_TYPE = 'text/csv;charset=utf-8;';
// Without this, Excel mangles non-ASCII like € into mojibake.
const UTF8_BOM = '﻿';

export function csvEscape(value: string): string {
  if (!/[",\n]/.test(value)) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

export function rowsToCsv(rows: string[][]): string {
  return rows.map((row) => row.map(csvEscape).join(',')).join('\n');
}

function scoreCell(score: number | null): string {
  return score === null ? '' : `${Math.round(score)}`;
}

function creativeScoreCells(
  creativeScore: CreativesListData['creatives'][number]['creativeScore'],
): string[] {
  if (creativeScore === undefined) return [];
  return [
    scoreCell(creativeScore.score),
    ...CREATIVE_SCORE_COMPONENT_ORDER.map((name) =>
      scoreCell(creativeScore.components[name]?.score ?? null),
    ),
  ];
}

function metricCell(
  value: number | null,
  uplift: number | null,
  format: CreativesListData['columns'][number]['format'],
  currencySymbol: string,
): string {
  const formatted = formatMetricValue(value, format, currencySymbol);
  return uplift === null ? formatted : `${formatted} (${formatDelta(uplift)})`;
}

export function creativesToCsv(data: CreativesListData, leadingKpiLabel: string): string {
  const metricColumns = data.columns;
  const hasCreativeScore = data.creatives.some((creative) => creative.creativeScore !== undefined);
  const componentLabel = creativeScoreComponentLabel(leadingKpiLabel);
  const scoreHeader = hasCreativeScore
    ? ['Creative Score', ...CREATIVE_SCORE_COMPONENT_ORDER.map((name) => componentLabel[name])]
    : [];
  const header = [
    'Creative',
    'Link',
    'Type',
    'Format',
    'Duration',
    'Active Days',
    'Spend Ratio',
    ...scoreHeader,
    ...metricColumns.map((column) => column.label),
  ];
  const totalsRow = [
    `Totals / averages · ${data.creatives.length}`,
    '',
    '',
    '',
    '',
    '',
    '',
    ...scoreHeader.map(() => ''),
    ...metricColumns.map((column) =>
      formatMetricValue(data.totals[column.name] ?? null, column.format, data.currencySymbol),
    ),
  ];
  const creativeRows = data.creatives.map((creative) => [
    creative.creativeId,
    creative.previewUrl || creative.thumbnailUrl,
    creative.creativeType ?? '',
    creative.format ?? '',
    formatDuration(creative.duration) ?? '',
    formatMetricValue(creative.activeDays, 'number', ''),
    formatMetricValue(creative.spendRatio, 'percentage', ''),
    ...creativeScoreCells(creative.creativeScore),
    ...metricColumns.map((column) =>
      metricCell(
        creative.metrics[column.name] ?? null,
        creative.uplift[column.name] ?? null,
        column.format,
        data.currencySymbol,
      ),
    ),
  ]);
  return rowsToCsv([header, totalsRow, ...creativeRows]);
}

export function downloadFile(filename: string, content: BlobPart, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadCsv(filename: string, content: string, type = CSV_MIME_TYPE): void {
  downloadFile(filename, UTF8_BOM + content, type);
}
